import {
  DIFFICULTY_LABELS,
  GENRE_LABELS,
  QUESTION_TYPE_LABELS,
  CLAIM_TYPE_LABELS,
  OPINION_PATTERN_EXAMPLE_MAX_CHAR_COUNT,
  OPINION_PATTERN_LABEL_RECOMMENDED_CHAR_COUNT,
  OPINION_PATTERN_DESCRIPTION_MAX_CHAR_COUNT,
  QuestionType,
  ClaimType,
  Genre,
} from "../types";

// --- パターンA: 属性指定 → テーマ候補を生成 ---

interface ThemeGenerationParams {
  genre: string;
  difficulty: number;
  questionType: QuestionType;
  claimType?: ClaimType;
}

// 仕様書: docs/specs/v2.0/difficulty-guidelines.md のClaim型判定基準テーブル
const CLAIM_DIFFICULTY_GUIDE = `【難易度の目安（主張を伝える）】
| 難易度 | テーマの抽象度 | 求められる思考 | 視点の数 | 前提知識 |
| 1（入門） | 日常・身近な話題 | 自分の体験で語れる | 1つでOK | 不要 |
| 2（初級） | 日常だが少し広い視野 | 体験＋簡単な理由づけ | 1〜2つ | ほぼ不要 |
| 3（中級） | 社会的テーマ | 分析・比較が必要 | 2〜3つ | あると有利 |
| 4（上級） | 抽象的・複合的テーマ | トレードオフの分析 | 3つ以上 | 必要 |
| 5（達人） | 多層的・倫理的テーマ | 深い分析＋価値判断の統合 | 多角的 | 専門知識が有利 |`;

// 仕様書: docs/specs/v2.0/difficulty-guidelines.md のPreference型判定基準テーブル
const PREFERENCE_DIFFICULTY_GUIDE = `【難易度の目安（思いを伝える）】
| 難易度 | テーマの性質 | 求められる言語化 | 掘り下げの深さ |
| 1（入門） | 具体的な二択や体験を問う | 「好きだから」でOK | 表面的でOK |
| 2（初級） | 日常的な好み・体験＋理由 | 理由を1つ説明 | 体験ベースの理由 |
| 3（中級） | 価値観が絡む選択・考えの整理 | 複数の観点から理由を説明 | ライフスタイル・価値観の比較 |
| 4（上級） | 抽象的な価値比較・内省的な問い | 自分の価値観を言語化 | トレードオフを認識した上での選択理由 |
| 5（達人） | 深い自己分析・哲学的な問い | 価値観の形成過程まで語る | 自己理解・内省の深さ |`;

// 仕様書: docs/specs/v2.0/difficulty-guidelines.md の簡易判定フロー
const DIFFICULTY_QUICK_FLOW = `【簡易判定フロー】まず以下の順で判定する:
1. 体験だけで答えられるか？ → Yes: 1〜2 / No: 3以上
2. 複数の視点を考える必要があるか？ → Yes: 3以上
3. 前提知識や深い内省が必要か？ → Yes: 4以上
4. 専門知識や哲学的思考が有利か？ → Yes: 5

※「身近・自分ごと」で当事者が自分の体験から賛否を言えるテーマは、社会的な話題に見えても1〜2に寄せる。「社会的文脈がある」という理由だけで安易に4以上にしない。`;

// 仕様書: docs/specs/v2.0/difficulty-guidelines.md のClaim型 参考例（Fact / Value / Policy）
const CLAIM_DIFFICULTY_EXAMPLES = `【Claim型の難易度別 参考例】
Fact（事実の解釈）:
- 1（入門）: 「朝食を食べる子は成績が良い」
- 2（初級）: 「運動は勉強の効率を上げる」
- 3（中級）: 「SNSは若者のコミュニケーション能力を低下させている」
- 4（上級）: 「AIの普及は雇用を増やすか減らすか」
- 5（達人）: 「少子化の主因は経済的要因か価値観の変化か」
Value（価値判断）:
- 1（入門）: 「手書きの手紙はメールより心がこもっている」
- 2（初級）: 「紙の本は電子書籍より優れている」
- 3（中級）: 「終身雇用は良い制度か」
- 4（上級）: 「経済成長と環境保護は両立できるか」
- 5（達人）: 「表現の自由とヘイトスピーチ規制はどちらが優先されるべきか」
Policy（行動提案）:
- 1（入門）: 「学校にスマホを持ち込んで良いか」
- 2（初級）: 「ゴミの分別を全国統一すべきか」
- 3（中級）: 「選挙の投票を義務化すべきか」
- 4（上級）: 「ベーシックインカムを導入すべきか」
- 5（達人）: 「臓器売買を合法化すべきか」`;

// 仕様書: docs/specs/v2.0/difficulty-guidelines.md のPreference型 参考例
const PREFERENCE_DIFFICULTY_EXAMPLES = `【Preference型の難易度別 参考例】
- 1（入門）: 「ご飯派？パン派？」「最近食べて美味しかったものは？」
- 2（初級）: 「旅行するなら海と山どっち？」「休日の過ごし方で一番好きなことは？」
- 3（中級）: 「都会暮らしと田舎暮らし、どちらが理想？」「今の仕事で一番やりがいを感じる瞬間は？」
- 4（上級）: 「安定した仕事とやりがいのある仕事、どちらを選ぶ？」「10年後の自分にアドバイスするなら何を伝える？」
- 5（達人）: 「人生で最も大切にしている価値観は何か、それはなぜか」「あなたにとって"成功"とは何か、その定義はどう変わってきたか」`;

export const generateThemePrompt = ({
  genre,
  difficulty,
  questionType,
  claimType,
}: ThemeGenerationParams): string => {
  const genreLabel = GENRE_LABELS[genre as Genre] || genre;
  const difficultyLabel = DIFFICULTY_LABELS[difficulty as keyof typeof DIFFICULTY_LABELS];
  const questionTypeLabel = QUESTION_TYPE_LABELS[questionType];

  const sections: string[] = [];

  sections.push(`以下の条件に合うテーマ（問い）を5つ提案してください。`);

  sections.push(`【条件】
- ジャンル: ${genreLabel}
- 難易度: ${difficulty}（${difficultyLabel}）
- 問題タイプ: ${questionTypeLabel}（${questionType}）${
    questionType === "claim" && claimType
      ? `\n- 主張タイプ: ${CLAIM_TYPE_LABELS[claimType]}（${claimType}）`
      : ""
  }`);

  sections.push(questionType === "claim" ? CLAIM_DIFFICULTY_GUIDE : PREFERENCE_DIFFICULTY_GUIDE);

  if (questionType === "claim") {
    sections.push(`【テーマの方向性】
- ユーザーが「自分の意見を持てる」問いにすること
- 賛成・反対どちらの立場も取りうる、議論の余地がある問い
- 一般論や常識を問うだけの問いにしない`);
  } else {
    sections.push(`【テーマの方向性】
- ユーザーが自分の好みや体験から語れる問いにすること
- 正解がなく、人によって答えが異なる問い
- 指定された難易度の「テーマの性質」に沿った問いにする`);
  }

  sections.push(`【出力形式】
1. （テーマ1）
2. （テーマ2）
3. （テーマ3）
4. （テーマ4）
5. （テーマ5）`);

  return sections.join("\n\n");
};

// --- パターンB: テーマ → 最適な属性を提案 ---

interface AttributeSuggestionParams {
  theme: string;
}

export const generateAttributeSuggestionPrompt = ({ theme }: AttributeSuggestionParams): string => {
  const sections: string[] = [];

  sections.push(
    `以下のテーマに最適なジャンル、難易度、問題タイプを提案してください。テーマ自体もより分かりやすい表現があれば改善してください。`
  );

  sections.push(`【テーマ】
${theme}`);

  sections.push(`【提案する属性】
1. ジャンル（以下から1つ選択）:
   ${Object.entries(GENRE_LABELS)
     .map(([key, label]) => `${label}（${key}）`)
     .join("、")}

2. 難易度（1〜5の整数）: 下記の【難易度の判定基準】に従って判定する

3. 問題タイプ:
   - claim（${QUESTION_TYPE_LABELS.claim}）: 議論・論証が可能な問い
   - preference（${QUESTION_TYPE_LABELS.preference}）: 好み・体験・感想など、正解のない問い

4. 主張タイプ（問題タイプがclaimの場合のみ）:
   - fact（${CLAIM_TYPE_LABELS.fact}）: 事実の解釈に基づく主張
   - value（${CLAIM_TYPE_LABELS.value}）: 価値判断（良し悪し・優劣）
   - policy（${CLAIM_TYPE_LABELS.policy}）: 行動・政策の提案`);

  sections.push(`【難易度の判定基準】
まず問題タイプ（claim / preference）を判定し、対応する基準で難易度を決める。

${DIFFICULTY_QUICK_FLOW}

${CLAIM_DIFFICULTY_GUIDE}

${CLAIM_DIFFICULTY_EXAMPLES}

${PREFERENCE_DIFFICULTY_GUIDE}

${PREFERENCE_DIFFICULTY_EXAMPLES}`);

  sections.push(`【判定のポイント】
- 「〜すべきか」「〜は正しいか」「〜か否か」→ claim
- 「〜派？」「どっちが好き？」「〜について教えて」「〜の経験は？」→ preference
- 主張タイプ（claim）の判定:
  - 事実・データ・因果関係の議論 → fact
  - 良し悪し・優劣・価値の議論 → value
  - 行動・制度・ルールの提案 → policy`);

  sections.push(`【出力形式】
改善テーマ: （改善後のテーマ。元のテーマで十分な場合はそのまま）
ジャンル: （日本語名）
難易度: （1〜5の数値）
問題タイプ: （claim / preference）
主張タイプ: （fact / value / policy）※claimの場合のみ
理由: （なぜこの属性が最適かの簡単な説明）`);

  return sections.join("\n\n");
};

// --- パターンC: 属性情報 → 背景を生成 ---

interface BackgroundGenerationParams {
  theme: string;
  genre: string;
  difficulty: number;
  questionType: QuestionType;
  claimType?: ClaimType;
  openingPatternId?: number;
  closingPatternId?: number;
}

export interface BackgroundOpeningPatternOption {
  id: number;
  label: string;
  description: string;
}

export const BACKGROUND_OPENING_PATTERN_OPTIONS: readonly BackgroundOpeningPatternOption[] = [
  { id: 1, label: "問いかけ", description: "問いかけから入る（読者への直接的な投げかけ）" },
  { id: 2, label: "統計・数字", description: "統計・数字・社会の変化から入る" },
  {
    id: 3,
    label: "対比",
    description: "対比から入る（過去と現在、AとB、理想と現実など）",
  },
  { id: 4, label: "歴史・文化", description: "歴史的・文化的事実から入る" },
  { id: 5, label: "感覚・違和感", description: "個人の感覚や違和感から入る" },
  {
    id: 6,
    label: "専門家・引用",
    description: "専門家・第三者の言説や引用から入る",
  },
  { id: 7, label: "仮想シナリオ", description: "仮想シナリオから入る（「もし〜だったら」）" },
] as const;

export interface BackgroundClosingPatternOption {
  id: number;
  label: string;
  description: string;
}

export const BACKGROUND_CLOSING_PATTERN_OPTIONS: readonly BackgroundClosingPatternOption[] = [
  {
    id: 1,
    label: "余韻",
    description: "やわらかな仮定で余韻を残す（例:「〜を見つめ直すきっかけになるかもしれません」）",
  },
  {
    id: 2,
    label: "投げかけ",
    description: "読者への問いかけで締める（例:「〜について少し立ち止まって考えてみませんか」）",
  },
  {
    id: 3,
    label: "気づき",
    description: "気づきや変化への期待で締める（例:「〜の意味を問い直す時間になるはずです」）",
  },
  {
    id: 4,
    label: "自問",
    description:
      "自分への問いとして締める（例:「自分にとってそれはどんな意味があるのか、改めて考えてみたい問いです」）",
  },
  {
    id: 5,
    label: "共有",
    description:
      "読者の体験や意見を聞きたい姿勢で締める（例:「あなたの考えを、ぜひ聞かせてください」）",
  },
  {
    id: 6,
    label: "仮定",
    description:
      "仮定から想像を促す（例:「もし〜だったとしたら、どんな景色が見えてくるでしょうか」）",
  },
  {
    id: 7,
    label: "委ね",
    description:
      "答えの多様性を肯定して読者に委ねる（例:「答えはひとつではないからこそ、ご自身の中で考えてみる価値がありそうです」）",
  },
  {
    id: 8,
    label: "体験想起",
    description:
      "読者自身の経験を思い出させて締める（例:「ご自身の経験を思い出しながら、考えてみてください」）",
  },
  {
    id: 9,
    label: "行動誘導",
    description:
      "軽い行動への誘いで締める（例:「今日の暮らしの中で、少し意識してみるのもよいかもしれません」）",
  },
  {
    id: 10,
    label: "再定義",
    description:
      "問いの意味を再定義して締める（例:「正解を求めるのではなく、自分の納得を探す問いとも言えそうです」）",
  },
] as const;

export const BACKGROUND_CHAR_GUIDE: Record<number, { min: number; max: number }> = {
  1: { min: 400, max: 500 },
  2: { min: 450, max: 600 },
  3: { min: 500, max: 700 },
  4: { min: 600, max: 850 },
  5: { min: 700, max: 1000 },
};

const CONTENT_CHAR_GUIDE: Record<number, { min: number; max: number }> = {
  1: { min: 300, max: 500 },
  2: { min: 400, max: 600 },
  3: { min: 500, max: 800 },
  4: { min: 700, max: 1000 },
  5: { min: 800, max: 1200 },
};

const BACKGROUND_DIFFICULTY_GUIDE = `【難易度別の背景説明の方向性】いずれも「立場の紹介」ではなく「考えるための情報」を提示する。
- 1（入門）: 身近な体験・日常シーンに即した情報を1つ示す。専門用語は使わない。読者が「自分ごと」として捉えられる距離感
- 2（初級）: 日常に関わる事実や簡単な違い・傾向を1つ示す。身近なデータや具体例を添えてもよい
- 3（中級）: 社会的な文脈となる情報を補足する。関連する調査結果・統計・一般的な事実を1〜2点紹介する
- 4（上級）: 前提となる文脈（社会背景・歴史的経緯・制度や法律など）を簡潔に補足し、判断に必要な情報を整理する
- 5（達人）: 多層的なテーマの判断材料を整理する。専門用語があれば軽く解説し、研究結果・法制度・経済/倫理/歴史などの観点から客観的な情報を示す`;

const BACKGROUND_QUESTION_TYPE_GUIDE = `【問題タイプ別の方向性】
- claim（主張を伝える）: テーマを判断するための客観的な情報（事実・データ・経緯・制度など）を整理して提示する。立場の代弁や賛否の誘導はしない
- preference（思いを伝える）: 共感や体験の入り口となる情報・状況を示す。正解がないことを前提に、過去の体験や価値観を振り返りたくなる素材を提供する`;

const BACKGROUND_CLAIM_TYPE_GUIDE = `【主張タイプ別の方向性（claim時のみ）】いずれも判断材料となる情報の提示に徹する。
- fact（事実）: 関連するデータ・調査結果・観測されている事実を示す。数値や出典の傾向に触れてもよい
- value（価値）: 判断の背景となる事実や、価値観に関わる一般的な知見・歴史的経緯を示す
- policy（政策）: 関連する制度・法律・過去の施策やその影響範囲など、提案を検討する材料を示す`;

const buildOpeningPatternsGuide = (selectedId?: number): string => {
  const selected =
    selectedId !== undefined
      ? BACKGROUND_OPENING_PATTERN_OPTIONS.find((option) => option.id === selectedId)
      : undefined;
  if (selected) {
    return `【導入の型】
以下の型で書き始めること:
- ${selected.description}`;
  }
  const list = BACKGROUND_OPENING_PATTERN_OPTIONS.map(
    (option, index) => `${index + 1}. ${option.description}`
  ).join("\n");
  return `【導入の型】
以下のいずれかから選んで書き始めること。同じ型ばかり連続させず、テーマに合うものを選ぶ:
${list}`;
};

const buildClosingPatternsGuide = (selectedId?: number): string => {
  const intro = `締めの一文は言い切り（「〜である」「〜となる」「〜だ」）を避け、敬語で余韻や問いかけを残す表現にすること。`;
  const selected =
    selectedId !== undefined
      ? BACKGROUND_CLOSING_PATTERN_OPTIONS.find((option) => option.id === selectedId)
      : undefined;
  if (selected) {
    return `【締めの型】
${intro}
以下の型で締めること:
- ${selected.description}`;
  }
  const list = BACKGROUND_CLOSING_PATTERN_OPTIONS.map(
    (option, index) => `${index + 1}. ${option.description}`
  ).join("\n");
  return `【締めの型】
${intro}
以下のいずれかから選んで締めること。同じ型ばかり連続させず、テーマに合うものを選ぶ:
${list}`;
};

const BACKGROUND_FORBIDDEN_PHRASES = `【避けるべき定型表現】
以下の決まり文句は使用しないこと:
- 「このテーマの面白さは〜」
- 「〜という問いに正解はありません」
- 「意見の分かれるテーマ（問題）です」「賛否が分かれます」など、意見が分かれること自体をわざわざ述べる表現（意見が分かれるのは前提なので明示不要）
- 「賛成派は〜、反対派は〜」のように立場ごとの主張を代弁する表現`;

export const generateBackgroundPrompt = ({
  theme,
  genre,
  difficulty,
  questionType,
  claimType,
  openingPatternId,
  closingPatternId,
}: BackgroundGenerationParams): string => {
  const genreLabel = GENRE_LABELS[genre as Genre] || genre;
  const difficultyLabel = DIFFICULTY_LABELS[difficulty as keyof typeof DIFFICULTY_LABELS];
  const questionTypeLabel = QUESTION_TYPE_LABELS[questionType];
  const backgroundChar = BACKGROUND_CHAR_GUIDE[difficulty];

  const sections: string[] = [];

  sections.push(`以下のテーマに対する「背景説明」を作成してください。`);

  sections.push(`【背景説明の役割（最重要）】
背景説明は「このテーマを考えるために役立つ情報の要約」です。賛成・反対などの立場を紹介したり、どちらが正しいかを示したりするものではありません（立場の検討は回答者が意見文の中で行います）。
- 書くべきこと: 関連する研究結果やデータ、法律・制度、過去から現在までの流れ（歴史的経緯・近年の変化）、用語の簡単な説明など、判断材料となる客観的な情報
- 書かないこと: 「賛成の立場では〜、反対の立場では〜」のような立場ごとの主張の代弁、結論の誘導、どちらか一方への肩入れ`);

  sections.push(`【テーマ】
${theme}`);

  sections.push(`【属性情報】
- ジャンル: ${genreLabel}
- 難易度: ${difficulty}（${difficultyLabel}）
- 問題タイプ: ${questionTypeLabel}（${questionType}）${
    questionType === "claim" && claimType
      ? `\n- 主張タイプ: ${CLAIM_TYPE_LABELS[claimType]}（${claimType}）`
      : ""
  }`);

  sections.push(BACKGROUND_DIFFICULTY_GUIDE);
  sections.push(BACKGROUND_QUESTION_TYPE_GUIDE);

  if (questionType === "claim") {
    sections.push(BACKGROUND_CLAIM_TYPE_GUIDE);
  }

  sections.push(buildOpeningPatternsGuide(openingPatternId));
  sections.push(buildClosingPatternsGuide(closingPatternId));
  sections.push(BACKGROUND_FORBIDDEN_PHRASES);

  sections.push(`【背景説明のルール】
- 文字数: ${backgroundChar.min}〜${backgroundChar.max}文字
- 読者が「自分も考えてみたい」「意見を書いてみたい」と感じる構成にする
- 立場の紹介ではなく、判断材料となる情報（研究結果・データ・法律や制度・過去から現在への流れなど）の要約を中心に書く
- 単なる事実の羅列ではなく、なぜこのテーマが考える価値があるのかが伝わるようにする
- 上で指定した【難易度別】【問題タイプ別】（claim時は【主張タイプ別】も）の方向性に沿って書く
- 誇張表現を使わない（「絶対に」「圧倒的に」「最も〜だ」「すべての人が」「必ず」のような極端な断定や強調を避け、落ち着いたトーンで書く）
- 結論を押し付けず、読者が自分で考える余白を残す
- 文末の句点「。」ごとに改行を入れ、話題が変わる箇所では空行を入れて読みやすくする`);

  sections.push(`【出力形式】
背景説明:
（背景説明文）`);

  return sections.join("\n\n");
};

// --- パターンD: 属性情報 → 課題文を生成（任意・Claim型向け） ---

interface SourceTextGenerationParams {
  theme: string;
  genre: string;
  difficulty: number;
  claimType?: ClaimType;
  background?: string;
}

export const generateSourceTextPrompt = ({
  theme,
  genre,
  difficulty,
  claimType,
  background,
}: SourceTextGenerationParams): string => {
  const genreLabel = GENRE_LABELS[genre as Genre] || genre;
  const difficultyLabel = DIFFICULTY_LABELS[difficulty as keyof typeof DIFFICULTY_LABELS];
  const contentChar = CONTENT_CHAR_GUIDE[difficulty];

  const sections: string[] = [];

  sections.push(
    `以下のテーマに対する「課題文」（筆者の意見文）を作成してください。※課題文は任意項目です。意見の根拠として具体的な主張文が必要なときに生成してください。`
  );

  sections.push(`【テーマ】
${theme}`);

  sections.push(`【属性情報】
- ジャンル: ${genreLabel}
- 難易度: ${difficulty}（${difficultyLabel}）${
    claimType ? `\n- 主張タイプ: ${CLAIM_TYPE_LABELS[claimType]}（${claimType}）` : ""
  }`);

  if (background) {
    sections.push(`【背景説明（参考）】
${background}`);
  }

  sections.push(`【課題文のルール】
- 文字数: ${contentChar.min}〜${contentChar.max}文字
- 筆者が明確な主張（意見）を1つ持ち、その根拠を論理的に展開する文章にする
- 読者への問いかけや質問文にせず、あくまで筆者個人の意見として書く
- 「〜しなさい」「〜を論じよ」のような試験の指示文にしない
- 読者に「知らなかった」「そういう見方があるのか」と思わせる発見がある
- 具体的な事例やデータを交えて説得力を持たせる
- 一方的な結論を押しつけず、読者が自分で考える余白を残す
- 段落間は空行で区切り、読みやすくする`);

  sections.push(`【出力形式】
課題文:
（筆者の意見文）`);

  return sections.join("\n\n");
};

// --- パターンD: 全情報 → 意見パターンを生成 ---

interface OpinionPatternGenerationParams {
  theme: string;
  genre: string;
  difficulty: number;
  questionType: QuestionType;
  claimType?: ClaimType;
  background?: string;
  sourceText?: string;
}

export const generateOpinionPatternPrompt = ({
  theme,
  difficulty,
  questionType,
  claimType,
  background,
  sourceText,
}: OpinionPatternGenerationParams): string => {
  const difficultyLabel = DIFFICULTY_LABELS[difficulty as keyof typeof DIFFICULTY_LABELS];

  const sections: string[] = [];

  sections.push(`以下の問題に対する意見パターンを作成してください。`);

  sections.push(`【テーマ】
${theme}`);

  if (background) {
    sections.push(`【背景説明】
${background}`);
  }

  if (sourceText) {
    sections.push(`【課題文】
${sourceText}`);
  }

  sections.push(`【難易度】
${difficulty}（${difficultyLabel}）`);

  if (questionType) {
    const questionTypeLabel = QUESTION_TYPE_LABELS[questionType];
    sections.push(`【問題タイプ】
${questionTypeLabel}（${questionType}）`);

    if (questionType === "claim" && claimType) {
      const claimTypeLabel = CLAIM_TYPE_LABELS[claimType];
      sections.push(`【主張タイプ】
${claimTypeLabel}（${claimType}）`);
    }
  }

  const patternConditions = buildPatternConditions(questionType, claimType);

  sections.push(`【条件】
- 各パターンのラベル: ${OPINION_PATTERN_LABEL_RECOMMENDED_CHAR_COUNT.min}〜${OPINION_PATTERN_LABEL_RECOMMENDED_CHAR_COUNT.max}文字
- 各パターンの具体例: ${OPINION_PATTERN_EXAMPLE_MAX_CHAR_COUNT}文字以内
- 各パターンの説明: ${OPINION_PATTERN_DESCRIPTION_MAX_CHAR_COUNT}文字以内
- 誇張表現を使わない（「絶対に」「圧倒的に」「最も〜だ」「すべての人が」「必ず」のような極端な断定や強調を避け、落ち着いたトーンで書く）
- 表面的でなく、少し深みのある内容にすること
${patternConditions}

【ラベルの書き方】
- テーマに即した短い立場表明にすること（長い文章にしない）
- 一目でどちら寄りの意見か分かるようにすること
- 良い例:
  テーマ「英語教育は小学校から必要か」→ 「早期導入賛成」「母語優先」「段階的導入」
  テーマ「ご飯派かパン派か」→ 「ご飯派」「パン派」「どちらも好き」
- 悪い例:
  「賛成意見」「反対意見」（何についての賛成/反対か不明）
  「毎日食べても飽きないご飯の安心感」（長すぎる）`);

  sections.push(`【出力形式】
${buildOutputFormat(questionType, claimType)}`);

  return sections.join("\n\n");
};

const buildPatternConditions = (questionType?: QuestionType, claimType?: ClaimType): string => {
  if (!questionType || questionType === "claim") {
    if (claimType === "fact") {
      return `- パターン数: 3つ
- 事実の解釈に基づく議論なので、以下の3つの立場を含めること:
  1. 出題文の事実解釈に賛成する立場（データや根拠で裏付け）
  2. 出題文の事実解釈に反対する立場（別のデータや解釈で反論）
  3. 別の視点からの解釈（両方の要素を踏まえた独自の分析）`;
    }
    if (claimType === "value") {
      return `- パターン数: 3つ
- 価値判断の議論なので、以下の3つの立場を含めること:
  1. 出題文の価値判断に賛成する立場（独自の評価基準や具体例で支持）
  2. 出題文の価値判断に反対する立場（別の基準や反例で反論）
  3. 別の価値基準からの意見（異なる視点からの評価）`;
    }
    if (claimType === "policy") {
      return `- パターン数: 3つ
- 政策・行動提案の議論なので、以下の3つの立場を含めること:
  1. 提案に賛成する立場（メリットや実現可能性を論じる）
  2. 提案に反対する立場（デメリットやコスト・障壁を論じる）
  3. 代替案の提案（別のアプローチや修正案を提示）`;
    }
    return `- パターン数: 3つ（賛成、反対、別の意見の3つの立場を1つずつ含めること）`;
  }

  if (questionType === "preference") {
    return `- パターン数: 2〜3つ
- 好み・体験・感想を問う問いなので、以下のような立場を含めること:
  1. 一方を好む立場（具体的な体験やエピソードで理由を語る）
  2. もう一方を好む立場（同上）
  3. （任意）両方好き・状況による、という立場
- 「好き」「嫌い」だけでなく、なぜそう感じるかの理由や体験を含めること`;
  }

  return `- パターン数: 3つ`;
};

const buildOutputFormat = (questionType?: QuestionType, claimType?: ClaimType): string => {
  if (questionType === "preference") {
    return `--- 意見パターン1 ---
ラベル:
（ラベル）

具体例:
（具体例）

説明:
（説明）

--- 意見パターン2 ---
ラベル:
（ラベル）

具体例:
（具体例）

説明:
（説明）

--- 意見パターン3（任意: どちらも / 状況による） ---
ラベル:
（ラベル）

具体例:
（具体例）

説明:
（説明）`;
  }

  const labels = getClaimPatternLabels(claimType);
  return `--- 意見パターン1（${labels[0]}） ---
ラベル:
（ラベル）

具体例:
（具体例）

説明:
（説明）

--- 意見パターン2（${labels[1]}） ---
ラベル:
（ラベル）

具体例:
（具体例）

説明:
（説明）

--- 意見パターン3（${labels[2]}） ---
ラベル:
（ラベル）

具体例:
（具体例）

説明:
（説明）`;
};

const getClaimPatternLabels = (claimType?: ClaimType): [string, string, string] => {
  switch (claimType) {
    case "fact":
      return ["賛成（事実裏付け）", "反対（別の解釈）", "別の視点"];
    case "value":
      return ["賛成（価値支持）", "反対（別の基準）", "別の価値観"];
    case "policy":
      return ["賛成（実現性）", "反対（デメリット）", "代替案"];
    default:
      return ["賛成", "反対", "別の意見"];
  }
};

// --- パターンF: テーマ → 画像生成プロンプト ---

// [Subject] [Action] [Composition] 以外の固定セクション
// scripts/lib/imagePromptTemplate.ts にも同じ内容を持たせている。
// 片方を変更したら必ずもう片方も揃えること。
//
// スタイルの狙い（良い例と悪い例の差の言語化）:
// - 良い例: 手描き風の線の揺らぎ、柔らかい陰影と紙のような質感、背景ぼかしによる
//   奥行き、1シーン1主役の寄った構図
// - 悪い例（避けたいもの）: 陰影も質感もない均一な線のフラットベクター素材風、
//   全面ピントの平板な画面、場面を並べた図解・インフォグラフィック風の分割構図
const IMAGE_PROMPT_FIXED_SECTIONS = `[Camera/Lighting] 明るめのくすんだパステル。窓からの自然光のような柔らかい光を感じさせ、背景は被写界深度で軽くぼかして奥行きを出す

[Color] 明るめのくすんだパステルを基調に、場面に合わせて自由に配色する。モチーフ本来の色は、くすんだトーンに調整して使ってよい
地面や道路、壁、空などの広い面にも必ず淡い色を置き、白のまま残さない
線の色は #5B5147（焦げ茶）で固定する
使ってはいけない色（NGリスト。生成結果を見て追記していく）:
- 蛍光色
- 彩度の高い鮮やかな原色（くすませれば使用可）

[Style] 手描き風の温かみのあるセミフラットイラスト。細めの柔らかいアウトラインに手描きらしいわずかな強弱や揺らぎを持たせる（機械的に均一なベクター線にしない）。塗りには柔らかい陰影と紙のような淡い質感を入れる（陰影のないベタ塗りにしない）。画面の隅々まで着色し、白やグレーの塗り残しを作らない（背景をぼかす場合も色は乗せたまま輪郭だけ柔らかくする）

[Character (人物を含める場合のみ)]
- 顔: 目は小さな点、眉毛あり、髪の毛あり、頬にほんのり赤み
- 配色: [Color] の基調（明るめのくすんだパステル）に合わせる
- 服や髪に皺やハイライトなどの簡単な陰影を入れる

[Negative] 文字・記号・ロゴ・透かしを一切含めない
(no text, no letters, no words, no captions, no watermark, no logo, no signature)
スマートフォンやパソコン、テレビなどの画面の中身を描き込まない。画面は淡い単色の面か、ごく単純な図形ひとつにとどめ、アプリ画面やボタン、ウィンドウを再現しない（描き込むと画面から要素がはみ出す、向きが反転する、文字らしき記号が現れるなどの破綻が起きる）
写実的な写真風にしない
フラットなベクター素材風（陰影なし、質感なし、均一な線のベタ塗り）にしない
画面を分割した比較構図やコマ割り、インフォグラフィック風のレイアウトにしない
未着色の面や白飛びした背景を残さない（線画のままの領域を作らない）`;

interface ImagePromptGenerationParams {
  theme: string;
}

export const generateImagePrompt = ({ theme }: ImagePromptGenerationParams): string => {
  return `[Canvas/Aspect] 16:9 cinematic

[Subject] ${theme}

[Action] 静止した構図

[Composition] 1つの場面に絞った一枚絵。画面の分割やコマ割りをしない。背景に観葉植物や本など生活感のある小物を少し添える

${IMAGE_PROMPT_FIXED_SECTIONS}`;
};

// --- パターンG: テーマと背景 → 画像生成プロンプトを設計 ---

interface ImagePromptDesignParams {
  theme: string;
  background?: string;
  sourceText?: string;
}

export const generateImagePromptDesignPrompt = ({
  theme,
  background,
  sourceText,
}: ImagePromptDesignParams): string => {
  const sections: string[] = [];

  sections.push(
    `以下のテーマと参考情報をもとに、お題のイメージ画像を作るための「画像生成AI用プロンプト」を設計してください。`
  );

  sections.push(`【テーマ】
${theme}`);

  if (background) {
    sections.push(`【背景説明（参考）】
${background}`);
  }

  if (sourceText) {
    sections.push(`【課題文（参考）】
${sourceText}`);
  }

  sections.push(`【設計の手順】
1. テーマと参考情報から、このお題を象徴する具体的なモチーフ（人物、物、場所、情景）の候補を3つ挙げる
2. それぞれの候補について「一枚の絵として伝わりやすいか」「特定の立場に偏らないか」を短く検討する
3. 最も適した候補を1つ選び、下記テンプレートの [Subject] [Action] [Composition] を具体的に埋める`);

  sections.push(`【設計のルール】
- テーマの文字面だけで判断せず、背景説明や課題文にある事実や情景をモチーフ選びに活かす
- 抽象的な概念（「自由」「価値観」「多様性」など）はそのまま描かせず、具体的な物や場面に置き換える
- 賛成・反対など特定の立場に偏った構図にしない（お題を眺めるための中立的な一枚にする）
- 文字や記号で意味を伝える構図にしない（画像内に文字は出せないため）。看板や黒板、メニュー、本の表紙、ポスターなど文字が描かれがちな物をモチーフや背景に含めない
- 1枚につき1シーンに絞る。対比が本質のテーマでも場面を並べず、象徴的な一場面にする
- 主役は原則1人（または1つ）。ただし人と人のやり取りそのものがテーマのとき（会話、相談、あいさつ、譲り合い、言いにくいことを伝える場面など）は2人まで描いてよい。その場合も場面は1つに保ち、2人が同じやり取りの中にいる構図にする
- 主役を人物と決めつけない。食べ物や道具など「物」が主役のテーマや、風景が主役のテーマでは人物を入れず、物や情景だけで画面を作る（人物がいない場合、[Character] セクションは適用されない）。人の行動や関係そのものがテーマのときだけ人物を主役にする
- [Composition] には、観葉植物や本など生活感のある小物を背景に少し添えることを含めてよい
- [Canvas/Aspect] [Camera/Lighting] [Color] [Style] [Character] [Negative] はテンプレートの内容を変更せずそのまま使う`);

  sections.push(`【出力形式】
まずモチーフ候補と選定理由を簡潔に示し、最後に以下のテンプレートを埋めた完成プロンプトを、画像生成AIにそのまま渡せる形で出力すること:

[Canvas/Aspect] 16:9 cinematic

[Subject] （選んだモチーフの具体的な描写）

[Action] （モチーフの動きや状態。静止した構図を基本とする）

[Composition] （画面構成。1つの場面に絞った一枚絵。画面の分割やコマ割りをしない）

${IMAGE_PROMPT_FIXED_SECTIONS}`);

  return sections.join("\n\n");
};
